import { useEffect, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { tenantName } from '../components/TenantLogo';
import { useTabRoute } from '../hooks/useTabRoute';
import { Card, StatCard, Badge, Button, ProgressBar, SkeletonBar, PageShell, MetricCard, ShareButton, LevelProgressBar, Table, Modal, TabBar as UITabBar, EmptyState as UIEmptyState } from '../components/ui';
import { T } from '../styles/tokens';
import { BRAND_COLORS, REFERRAL_ACCENT } from '../constants/brands';

// ─── Types ────────────────────────────────────────────────────────────

interface ReferralData {
  referrals: any[];
  total: number;
  level: {
    level_name: string; badge: string; base_reward_usd: number;
    min_referrals: number; max_referrals: number | null; multiplier: number; monthly_bonus_usd: number;
  };
  missions: any[];
  ranking: any[];
}

interface WalletData {
  wallet: {
    balance_usd: number; balance_tokens: number; total_earned_usd: number;
    total_redeemed_usd: number; level: string; total_referrals: number; active_referrals: number;
  };
  transactions: any[];
  total_transactions: number;
}

// ─── State ────────────────────────────────────────────────────────────

const referralData = signal<ReferralData | null>(null);
const walletData = signal<WalletData | null>(null);
const referralLink = signal<string>('');
const loading = signal(true);
const activeTab = signal<'indicados' | 'missoes' | 'ranking' | 'carteira'>('indicados');
const copied = signal(false);
const creating = signal(false);
const showShareModal = signal(false);
const animateIn = signal(false);

async function fetchAll() {
  loading.value = true;
  try {
    const [refData, walData] = await Promise.all([
      api.get<ReferralData>('/cloud/referrals'),
      api.get<WalletData>('/cloud/referral-wallet'),
    ]);
    referralData.value = refData;
    walletData.value = walData;
    // Set link from existing referrals
    if (refData.referrals?.length > 0) {
      const withLink = refData.referrals.find((r: any) => r.referral_link);
      if (withLink) referralLink.value = withLink.referral_link;
    }
  } catch (err) {
    console.error('Failed to fetch referral data', err);
  }
  loading.value = false;
  setTimeout(() => { animateIn.value = true; }, 50);
}

async function createLink() {
  creating.value = true;
  try {
    const res = await api.post<{ referral: { referral_link: string; referral_code: string } }>('/cloud/referrals', {});
    referralLink.value = res.referral.referral_link;
    await fetchAll();
  } catch (err) { console.error('Failed to create referral link', err); }
  creating.value = false;
}

function copyLink() {
  if (referralLink.value) {
    navigator.clipboard.writeText(referralLink.value);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 2500);
  }
}

const SHARE_MESSAGE = (link: string) =>
 `Conheça o ${tenantName()} — IA avançada com Router LLM!\n\nUse meu link e ganhe créditos grátis:\n${link}`;

function shareWhatsApp() {
  window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE(referralLink.value))}`, '_blank');
}
function shareTelegram() {
 window.open(`https://t.me/share/url?url=${encodeURIComponent(referralLink.value)}&text=${encodeURIComponent(`Conheça o ${tenantName()} — IA avançada com Router LLM! Use meu link e ganhe créditos grátis`)}`, '_blank');
}
function shareEmail() {
  window.open(`mailto:?subject=${encodeURIComponent(`Conheça o ${tenantName()}!`)}&body=${encodeURIComponent(SHARE_MESSAGE(referralLink.value))}`, '_blank');
}
function shareTwitter() {
  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_MESSAGE(referralLink.value))}`, '_blank');
}

// ─── Level Config ─────────────────────────────────────────────────────

const LEVELS = [
 { name: 'starter', label: 'Starter', icon: '', color: T.textSecondary, min: 0, max: 5 },
 { name: 'pro', label: 'Pro', icon: '', color: T.primary, min: 6, max: 20 },
 { name: 'elite', label: 'Elite', icon: '', color: T.warning, min: 21, max: 50 },
 { name: 'master', label: 'Master', icon: '', color: T.danger, min: 51, max: null },
];

const STATUS_MAP: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info' | 'neutral' }> = {
  pending: { label: 'Pendente', variant: 'warning' },
  signup: { label: 'Cadastrou', variant: 'info' },
  activated: { label: 'Ativo', variant: 'success' },
  paid: { label: 'Pagante', variant: 'success' },
  blocked: { label: 'Bloqueado', variant: 'error' },
  churned: { label: 'Inativo', variant: 'neutral' },
};

// ─── Main Component ───────────────────────────────────────────────────

const REFERRAL_TAB_IDS = ['indicados', 'missoes', 'ranking', 'carteira'] as const;

export function ReferralDashboardPage() {
  useTabRoute(activeTab, '/referral', 'indicados', REFERRAL_TAB_IDS);
  useEffect(() => { fetchAll(); }, []);

  if (loading.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

  const data = referralData.value;
  const wallet = walletData.value?.wallet;
  const level = data?.level;
  const currentLevel = LEVELS.find(l => l.name === (level?.level_name || 'starter')) || LEVELS[0];
  const currentIdx = LEVELS.indexOf(currentLevel);
  const nextLevel = currentIdx < LEVELS.length - 1 ? LEVELS[currentIdx + 1] : null;
  const totalRefs = wallet?.total_referrals || 0;
  const levelProgress = nextLevel
    ? ((totalRefs - currentLevel.min) / (nextLevel.min - currentLevel.min)) * 100
    : 100;

  return (
    <div style={{
      padding: '20px 16px', maxWidth: 960, margin: '0 auto',
      opacity: animateIn.value ? 1 : 0, transform: animateIn.value ? 'translateY(0)' : 'translateY(12px)',
      transition: 'all 0.4s ease',
    }}>
      {/* Hero Section */}
      <HeroSection hasLink={!!referralLink.value} />

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard
          icon={currentLevel.icon}
          label="Seu Nível"
          value={currentLevel.label.toUpperCase()}
          accent={currentLevel.color}
          sub={nextLevel ? `${totalRefs}/${nextLevel.min} para ${nextLevel.label}` : 'Nível máximo!'}
        />
        <StatCard
 icon=""
          label="Saldo Disponível"
          value={`$${(wallet?.balance_usd || 0).toFixed(2)}`}
          accent={T.success}
          sub={`${(wallet?.balance_tokens || 0).toLocaleString()} tokens`}
        />
        <StatCard
 icon=""
          label="Total Ganho"
          value={`$${(wallet?.total_earned_usd || 0).toFixed(2)}`}
          accent={T.primary}
          sub={`Resgatado: $${(wallet?.total_redeemed_usd || 0).toFixed(2)}`}
        />
        <StatCard
 icon=""
          label="Indicações"
          value={`${wallet?.active_referrals || 0}/${totalRefs}`}
          accent={T.textSecondary}
          sub={`Recompensa: $${level?.base_reward_usd || 10} × ${level?.multiplier || 1}x`}
        />
      </div>

      {/* Level Progress */}
      <Card style={{ marginBottom: 20 }}>
        <LevelProgressBar levels={LEVELS} currentLevel={currentLevel} nextLevel={nextLevel} progress={levelProgress} />
      </Card>

      {/* Share CTA */}
      <ShareSection />

      {/* Tabs */}
      <TabBar />

      {/* Tab Content */}
      <div style={{
        opacity: animateIn.value ? 1 : 0, transition: 'opacity 0.3s ease 0.2s',
      }}>
        {activeTab.value === 'indicados' && <ReferralsTab referrals={data?.referrals || []} />}
        {activeTab.value === 'missoes' && <MissionsTab missions={data?.missions || []} />}
        {activeTab.value === 'ranking' && <RankingTab ranking={data?.ranking || []} />}
        {activeTab.value === 'carteira' && <WalletTab />}
      </div>

      {/* Programa de Afiliados */}
      <AffiliateSection />

      {/* Share Modal */}
      {showShareModal.value && <ShareModal />}
    </div>
  );
}

// ─── Affiliate Section ────────────────────────────────────────────────

function AffiliateSection() {
  const [status, setStatus] = useState<'loading' | 'none' | 'pending' | 'approved' | 'rejected' | 'suspended'>('loading');
  const [rejection, setRejection] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<any>('/affiliates/me');
        const aff = res?.affiliate || res?.data?.affiliate;
        if (aff) setStatus(aff.status);
        else setStatus('none');
        if (aff?.rejection_reason) setRejection(aff.rejection_reason);
      } catch {
        setStatus('none');
      }
    })();
  }, []);

  if (status === 'loading') return null;

  return (
    <Card style={{ marginTop: 20, overflow: 'hidden', position: 'relative' }}
          accent={`linear-gradient(90deg, ${T.purpleHover}, ${REFERRAL_ACCENT.rosa})`}>
      <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, background: 'radial-gradient(circle, rgba(124,58,237,0.15), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Programa de Afiliados</div>
        <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>
          Ganhe comissões sobre <strong>todos os pagamentos</strong> dos clientes que você trouxer — LLM e assinaturas!
        </div>

        {status === 'none' && (
          <a href="/affiliates" style={{
            display: 'inline-block', padding: '12px 24px',
            background: `linear-gradient(135deg, ${T.purpleHover}, ${T.primary})`,
            color: T.text, fontWeight: 700, fontSize: 15,
            borderRadius: 10, textDecoration: 'none', textAlign: 'center',
            width: '100%', boxSizing: 'border-box',
          }}>
            Quero ser Afiliado
          </a>
        )}

        {status === 'pending' && (
          <div style={{ background: T.warningBg, border: `1px solid ${T.warningBgStrong}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, color: T.warning }}>Solicitação em análise</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Você será notificado quando for aprovado!</div>
            </div>
          </div>
        )}

        {status === 'approved' && (
          <a href="/affiliates" style={{ display: 'block', textDecoration: 'none' }}>
            <div style={{ background: T.successBg, border: `1px solid ${T.successBg}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: T.success }}>Você é afiliado!</div>
                <div style={{ fontSize: 12, color: T.textMuted }}>Acesse seu painel para ver campanhas e ganhos</div>
              </div>
              <span style={{ fontSize: 18, color: T.success }}>→</span>
            </div>
          </a>
        )}

        {status === 'rejected' && (
          <div>
            <div style={{ background: T.dangerBg, border: `1px solid ${T.dangerBg}`, borderRadius: 10, padding: '12px 16px', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, color: T.danger, marginBottom: 4 }}>Solicitação negada</div>
              {rejection && <div style={{ fontSize: 12, color: T.textMuted }}>Motivo: {rejection}</div>}
            </div>
            <a href="/affiliates" style={{
              display: 'inline-block', padding: '10px 20px',
              background: `linear-gradient(135deg, ${T.purpleHover}, ${T.primary})`,
              color: T.text, fontWeight: 700, fontSize: 13,
              borderRadius: 10, textDecoration: 'none', textAlign: 'center',
              width: '100%', boxSizing: 'border-box',
            }}>
              Tentar novamente
            </a>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Hero Section ─────────────────────────────────────────────────────

function HeroSection({ hasLink }: { hasLink: boolean }) {
  return (
    <Card style={{ marginBottom: 20, overflow: 'hidden', position: 'relative' }}
          accent={`linear-gradient(90deg, ${T.primary}, ${T.accent}, ${REFERRAL_ACCENT.azul})`}>
      {/* Glow effect */}
      <div style={{
        position: 'absolute', top: -60, right: -60, width: 200, height: 200,
        background: `radial-gradient(circle, ${T.accentBg}, transparent 70%)`,
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
 <span style={{ fontSize: 28 }}></span>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
              Indique & Ganhe
            </h1>
            <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
              Ganhe créditos reais em USD para usar no Router LLM
            </p>
          </div>
        </div>

        {/* 3 Steps */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 16,
        }}>
          {[
 { step: '1', icon: '', title: 'Gere seu link', desc: 'Crie seu link único de indicação' },
 { step: '2', icon: '', title: 'Compartilhe', desc: 'Envie para amigos via WhatsApp, Telegram...' },
 { step: '3', icon: '', title: 'Ganhe créditos', desc: 'Receba até $50 por indicação ativa!' },
          ].map((s, i) => (
            <div key={i} style={{
              textAlign: 'center', padding: '14px 8px',
              background: T.primaryBg, borderRadius: 12,
              border: `1px solid ${T.primaryBg}`,
              transition: 'all 0.2s',
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800, color: T.primary, marginBottom: 4,
                background: T.primaryBg, display: 'inline-block',
                padding: '2px 8px', borderRadius: 10,
              }}>PASSO {s.step}</div>
              <div style={{ fontSize: 24, margin: '6px 0' }}>{s.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>{s.title}</div>
              <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.3 }}>{s.desc}</div>
            </div>
          ))}
        </div>

        {!hasLink && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button size="lg" onClick={createLink} disabled={creating.value}
              style={{ background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`, width: '100%', justifyContent: 'center' }}>
 {creating.value ? 'Gerando...' : 'Gerar Meu Link de Indicação'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Share Section ────────────────────────────────────────────────────

function ShareSection() {
  if (!referralLink.value) return null;

  return (
    <Card style={{ marginBottom: 20 }}
          accent={`linear-gradient(90deg, ${T.primary}, ${T.accent})`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
 <span style={{ fontSize: 18 }}></span>
        <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Seu Link de Indicação</span>
      </div>

      {/* Link box */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        background: T.bgInput, borderRadius: 10,
        border: `1px solid ${T.border}`, marginBottom: 12,
      }}>
        <span style={{
          flex: 1, fontSize: 13, color: T.textMuted,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontFamily: T.fontMono,
        }}>
          {referralLink.value}
        </span>
        <Button size="sm" onClick={copyLink}
          variant={copied.value ? 'secondary' : 'primary'}
          style={copied.value ? { background: T.successBg, color: T.success, border: `1px solid ${T.successBg}` } : {}}>
 {copied.value ? 'Copiado!' : 'Copiar'}
        </Button>
      </div>

      {/* Share buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
 <ShareButton icon="" label="WhatsApp" color={BRAND_COLORS.whatsapp} onClick={shareWhatsApp} />
 <ShareButton icon="" label="Telegram" color={BRAND_COLORS.telegram} onClick={shareTelegram} />
 <ShareButton icon="" label="Email" color={T.accent} onClick={shareEmail} />
 <ShareButton icon="" label="Twitter" color={BRAND_COLORS.twitter} onClick={shareTwitter} />
 <ShareButton icon="" label="Mais" color={T.textMuted} onClick={() => { showShareModal.value = true; }} />
      </div>
    </Card>
  );
}

// ShareButton agora importado de ../components/ui

// ─── Tab Bar ──────────────────────────────────────────────────────────
// Usa TabBar de ../components/ui (variant="pill")

function TabBar() {
  const tabs = [
    { id: 'indicados', icon: <span>👥</span>, label: 'Indicados' },
    { id: 'missoes', icon: <span>🎯</span>, label: 'Missões' },
    { id: 'ranking', icon: <span>🏆</span>, label: 'Ranking' },
    { id: 'carteira', icon: <span>💰</span>, label: 'Carteira' },
  ];

  return (
    <UITabBar
      tabs={tabs}
      active={activeTab.value}
      onChange={(id) => { activeTab.value = id as any; }}
      variant="pill"
    />
  );
}

// ─── Referrals Tab (MIGRADO: <table> nativa → Table ui/) ────────────────

function ReferralsTab({ referrals }: { referrals: any[] }) {
  if (!referrals.length) {
    return (
      <EmptyState
        icon="👥"
        title="Nenhuma indicação ainda"
        description="Compartilhe seu link e comece a ganhar créditos agora!"
        ctaLabel="Compartilhar Link"
        ctaAction={() => { showShareModal.value = true; }}
      />
    );
  }

  const columns = [
    {
      key: 'referree_id',
      label: 'Indicado',
      render: (val: any, row: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: `linear-gradient(135deg, ${T.accent}, ${T.purple})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14,
          }}>👤</div>
          <span style={{ fontFamily: T.fontMono, fontSize: 12 }}>
            {val?.substring(0, 8) || '—'}...
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (val: any, row: any) => {
        const status = STATUS_MAP[val] || { label: val, variant: 'neutral' as const };
        return (
          <Badge variant={status.variant} dot pulseDot={val === 'activated'}>
            {status.label}
          </Badge>
        );
      },
    },
    {
      key: 'source',
      label: 'Fonte',
      render: (val: any) => val || 'link',
    },
    {
      key: 'total_paid',
      label: 'Recompensa',
      render: (val: any) => (
        <span style={{ fontSize: 14, fontWeight: 700, color: T.success }}>
          ${(val || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Data',
      render: (val: any) => (
        <span style={{ fontSize: 12, color: T.textMuted }}>
          {new Date(val).toLocaleDateString('pt-BR')}
        </span>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      data={referrals}
      emptyText="Nenhuma indicação ainda"
      striped
    />
  );
}

// ─── Missions Tab ─────────────────────────────────────────────────────

function MissionsTab({ missions }: { missions: any[] }) {
  if (!missions.length) {
    return (
      <EmptyState
 icon=""
        title="Nenhuma missão disponível"
        description="Gere seu link de indicação para desbloquear missões e ganhar recompensas extras!"
 ctaLabel="Gerar Link"
        ctaAction={createLink}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {missions.map((m: any) => {
        const pct = m.target_value > 0 ? Math.min((m.current_value / m.target_value) * 100, 100) : 0;
        const isComplete = m.status === 'completed';
        const isClaimed = m.status === 'claimed';

        return (
          <Card key={m.id} highlight={isComplete}
            style={isComplete ? { border: `1px solid ${T.successBg}` } : {}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
 <span style={{ fontSize: 18 }}>{isClaimed ? '' : isComplete ? '' : ''}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{m.title}</span>
                </div>
                <p style={{ fontSize: 12, color: T.textMuted, margin: 0, lineHeight: 1.4 }}>
                  {m.description}
                </p>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {m.reward_usd > 0 && (
                  <div style={{ fontSize: 20, fontWeight: 800, color: T.success }}>+${m.reward_usd}</div>
                )}
                {m.reward_type === 'cloud_free' && (
 <Badge variant="warning">Cloud Grátis</Badge>
                )}
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <ProgressBar
                  value={pct}
                  height={8}
                  color={isComplete ? T.success : undefined}
                  animated={isComplete && !isClaimed}
                />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: isComplete ? T.success : T.textMuted, whiteSpace: 'nowrap' }}>
                {m.current_value}/{m.target_value}
              </span>
            </div>

            {isComplete && !isClaimed && (
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={async () => {
                  try {
                    await api.post(`/cloud/referrals/missions/${m.id}/claim`, {});
                    await fetchAll();
                  } catch (err) { console.error(err); }
                }}
                  style={{ background: T.success, width: '100%', justifyContent: 'center' }}>
 Resgatar Recompensa
                </Button>
              </div>
            )}

            {m.expires_at && !isClaimed && !isComplete && (
              <div style={{ marginTop: 8, fontSize: 11, color: T.textMuted }}>
 Expira: {new Date(m.expires_at).toLocaleDateString('pt-BR')}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ─── Ranking Tab ──────────────────────────────────────────────────────

function RankingTab({ ranking }: { ranking: any[] }) {
 const medals = ['', '', ''];

  if (!ranking.length) {
    return (
      <EmptyState
 icon=""
        title="Ranking vazio"
        description="Seja o primeiro a indicar e conquiste o topo!"
 ctaLabel="Começar a Indicar"
        ctaAction={() => { if (!referralLink.value) createLink(); else showShareModal.value = true; }}
      />
    );
  }

  return (
    <Card noPadding>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
 <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Top Parceiros</span>
      </div>
      {ranking.map((r: any, i: number) => {
        const lvl = LEVELS.find(l => l.name === r.level) || LEVELS[0];
        const isTop3 = i < 3;
        return (
          <div key={r.user_id} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
            borderBottom: `1px solid ${T.borderSubtle}`,
            background: isTop3 ? `${lvl.color}06` : 'transparent',
            transition: 'background 0.15s',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: isTop3 ? 20 : 14,
              fontWeight: 800, color: isTop3 ? lvl.color : T.textMuted,
              background: isTop3 ? `${lvl.color}15` : T.bgCard,
            }}>
              {medals[i] || `#${i + 1}`}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.fontMono }}>
                {r.user_id.substring(0, 8)}...
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <Badge variant={r.level === 'master' ? 'error' : r.level === 'elite' ? 'warning' : r.level === 'pro' ? 'info' : 'neutral'}>
                  {lvl.icon} {lvl.label}
                </Badge>
                <span style={{ fontSize: 11, color: T.textMuted }}>{r.total_referrals} indicações</span>
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: T.success }}>
                ${(r.total_earned_usd || 0).toFixed(2)}
              </div>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

// ─── Wallet Tab ───────────────────────────────────────────────────────

function WalletTab() {
  const wallet = walletData.value;
  if (!wallet) return null;

  return (
    <div>
      {/* Wallet Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
 <StatCard icon="" label="Saldo" value={`$${wallet.wallet.balance_usd.toFixed(2)}`}
          accent={T.success} sub={`${wallet.wallet.balance_tokens.toLocaleString()} tokens`} />
 <StatCard icon="" label="Total Ganho" value={`$${wallet.wallet.total_earned_usd.toFixed(2)}`}
          accent={T.primary} />
 <StatCard icon="" label="Resgatado" value={`$${wallet.wallet.total_redeemed_usd.toFixed(2)}`}
          accent={T.textSecondary} />
      </div>

      {/* Redeem Button */}
      {wallet.wallet.balance_usd > 0 && (
        <Card highlight style={{ marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 8 }}>
            Converta seus créditos em tokens Router LLM
          </div>
          <Button size="lg" onClick={async () => {
            try {
              await api.post('/cloud/referral-wallet/redeem', { amount_usd: wallet.wallet.balance_usd });
              await fetchAll();
            } catch (err) { console.error(err); }
          }}
            style={{ width: '100%', justifyContent: 'center' }}>
 Resgatar ${wallet.wallet.balance_usd.toFixed(2)} → {wallet.wallet.balance_tokens.toLocaleString()} tokens
          </Button>
        </Card>
      )}

      {/* Transaction History */}
      <Card noPadding>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
 <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Histórico de Transações</span>
        </div>

        {wallet.transactions.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>
            Nenhuma transação ainda
          </div>
        ) : (
          <div>
            {wallet.transactions.map((tx: any) => {
              const isPositive = tx.amount_usd >= 0;
              return (
                <div key={tx.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
                  borderBottom: `1px solid ${T.borderSubtle}`,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: isPositive ? T.successBg : T.dangerBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16,
                  }}>
 {isPositive ? '' : ''}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                      {tx.description || tx.type}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                      {new Date(tx.created_at).toLocaleDateString('pt-BR')} · Saldo: ${tx.balance_after.toFixed(2)}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 15, fontWeight: 800,
                    color: isPositive ? T.success : T.danger,
                  }}>
                    {isPositive ? '+' : ''}{tx.amount_usd.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────

// Usa EmptyState de ../components/ui

function EmptyState({ icon, title, description, ctaLabel, ctaAction }: {
  icon: string; title: string; description: string; ctaLabel?: string; ctaAction?: () => void;
}) {
  return (
    <Card style={{ textAlign: 'center', padding: 0 }}>
      <UIEmptyState
        icon={<span style={{ fontSize: 48, filter: 'grayscale(0.3)', opacity: 0.9 }}>{icon}</span>}
        title={title}
        description={description}
        action={ctaLabel && ctaAction ? <Button onClick={ctaAction}>{ctaLabel}</Button> : undefined}
        style={{ padding: '48px 24px' }}
      />
    </Card>
  );
}

// ─── Share Modal (MIGRADO: Modal custom → Modal ui/) ────────────────

function ShareModal() {
  const link = referralLink.value;

  return (
    <Modal
      open={showShareModal.value}
      onClose={() => { showShareModal.value = false; }}
      title="🔗 Compartilhar Link"
      width={440}
    >
      {/* Link preview */}
      <div style={{
        padding: '12px 14px', background: T.bgInput, borderRadius: 10,
        border: `1px solid ${T.border}`, marginBottom: 16,
        fontFamily: T.fontMono, fontSize: 12, color: T.textMuted,
        wordBreak: 'break-all',
      }}>
        {link}
      </div>

      {/* Share options */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[
          { icon: '📱', label: 'WhatsApp', color: BRAND_COLORS.whatsapp, onClick: shareWhatsApp },
          { icon: '✈️', label: 'Telegram', color: BRAND_COLORS.telegram, onClick: shareTelegram },
          { icon: '📧', label: 'Email', color: T.accent, onClick: shareEmail },
          { icon: '🐦', label: 'Twitter/X', color: BRAND_COLORS.twitter, onClick: shareTwitter },
        ].map(s => (
          <Button key={s.label} variant="ghost" onClick={s.onClick} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
            borderRadius: 12, border: `1px solid ${s.color}25`,
            background: `${s.color}08`,
            color: T.text, fontSize: 13, fontWeight: 600,
          }}>
            <span style={{ fontSize: 20 }}>{s.icon}</span>
            {s.label}
          </Button>
        ))}
      </div>

      {/* Copy button */}
      <Button size="lg" onClick={() => { copyLink(); setTimeout(() => { showShareModal.value = false; }, 1000); }}
        variant={copied.value ? 'secondary' : 'primary'}
        style={{ width: '100%', justifyContent: 'center', ...(copied.value ? { background: T.successBg, color: T.success, border: `1px solid ${T.successBg}` } : {}) }}>
        {copied.value ? '✓ Link Copiado!' : '📋 Copiar Link'}
      </Button>

      {/* Pre-written message */}
      <div style={{ marginTop: 16, padding: '12px 14px', background: T.bgInput, borderRadius: 10, border: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase' }}>
          💬 Mensagem sugerida
        </div>
        <div style={{ fontSize: 12, color: T.text, lineHeight: 1.5 }}>
          {SHARE_MESSAGE(link)}
        </div>
      </div>
    </Modal>
  );
}
